/**
 * Table row selection helpers for multi-select functionality in configuration lists
 * These functions are generic and can be used across different modules
 */

/**
 * Get item IDs from selected row keys (row index strings or record ids).
 * @param {Array} selectedRowKeys
 * @param {Array} data
 * @param {Function} [filterFn]
 */
export const getSelectedItemIds = (selectedRowKeys, data, filterFn = null) => {
  return selectedRowKeys
    .map((key) => {
      const index = parseInt(key, 10);
      let item = !Number.isNaN(index) && data[index] != null ? data[index] : null;

      if (!item && data?.length) {
        item = data.find((row) => row?.id != null && String(row.id) === String(key));
      }

      if (!item) return null;

      if (filterFn && !filterFn(item)) return null;

      return item.id;
    })
    .filter(Boolean);
};

/**
 * Check if an item is disabled (not active)
 * @param {Object} item - Item object with isActive property
 * @returns {boolean} True if item is disabled
 */
export const isItemDisabled = (item) => {
  return item.isActive !== true && item.isActive !== 1;
};

/**
 * Check if an item is enabled (active)
 * @param {Object} item - Item object with isActive property
 * @returns {boolean} True if item is enabled
 */
export const isItemEnabled = (item) => {
  return item.isActive === true || item.isActive === 1;
};

/**
 * Keep only selected elements that are visible on current page
 * This ensures selection consistency when pagination or filters change
 * @param {Array} selectedRowKeys - Current selected row keys
 * @param {Array} data - Current page data
 * @returns {Object} { visibleKeys, visibleRows } - Filtered selection
 */
export const filterSelectionToVisibleItems = (selectedRowKeys, data) => {
  if (selectedRowKeys.length === 0 || !data || data.length === 0) {
    return { visibleKeys: selectedRowKeys, visibleRows: [] };
  }

  const currentPageData = data || [];
  const visibleIndices = currentPageData.map((_, index) => index.toString());

  const visibleKeys = selectedRowKeys.filter((key) => visibleIndices.includes(key));

  const visibleRows = selectedRowKeys
    .map((key) => {
      const index = parseInt(key, 10);
      return data[index];
    })
    .filter((row, index) => {
      const originalKey = selectedRowKeys[index];
      return visibleIndices.includes(originalKey);
    });

  return { visibleKeys, visibleRows };
};
